import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchDiff, fetchDiffs, fetchDiffsNavigation, updateEventVerified, updateEventPartiallyCorrect, updateEventInvalid, updateEventType, updateEventDescription, mergeEvents, deleteEvent, cloneEvent } from '@/utils/graphqlClient';
import { supabase } from '@/integrations/supabase/client';

export interface EventChange {
  id: string;
  event_type?: string;
  slug?: string;
  description?: string;
  before_extracts?: any;
  after_extract?: any;
  verified?: boolean;
  partially_correct?: boolean;
  invalid?: boolean;
  asset_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PageData {
  id: string;
  subslug?: string;
  pricing_page_url?: string;
  company?: {
    id: string;
    slug: string;
    url?: string;
    logo_url?: string;
  };
}

export interface DiffDetail {
  id: string;
  page_id: string;
  slug: string;
  v_before: string;
  v_after: string;
  asset_id?: string;
  hook?: string;
  description?: string;
  published?: boolean;
  created_at?: string;
  updated_at?: string;
  changes: EventChange[];
  page?: PageData;
}

export interface DiffSummary {
  id: string;
  slug: string;
  changes: Array<{
    id: string;
    event_type?: string;
    verified?: boolean;
    partially_correct?: boolean;
    invalid?: boolean;
  }>;
}

/**
 * Helper function to detect if a string is a UUID
 */
function isUUID(str: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Hook for fetching a single diff by ID or slug (uses direct database call)
 * - If diffId is a UUID, fetches by ID
 * - If diffId is not a UUID, treats it as a slug and fetches the most recent diff for that company
 */
export function useDiff(diffId: string, placeholderData?: DiffDetail) {
  const isId = isUUID(diffId);

  return useQuery({
    queryKey: ['diff', diffId, isId ? 'id' : 'slug'],
    queryFn: async (): Promise<DiffDetail> => {
      let newsData: any;

      if (isId) {
        // Fetch by ID
        const { data, error: newsError } = await supabase
          .from('app_news')
          .select('*')
          .eq('id', diffId)
          .single();

        if (newsError) {
          console.error('Error fetching news by ID:', newsError);
          throw new Error(`Failed to fetch diff: ${newsError.message}`);
        }

        newsData = data;
      } else {
        // Fetch by slug (get most recent diff for this company)
        const { data, error: newsError } = await supabase
          .from('app_news')
          .select('*')
          .eq('slug', diffId)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (newsError) {
          console.error('Error fetching news by slug:', newsError);
          throw new Error(`Failed to fetch diff for ${diffId}: ${newsError.message}`);
        }

        newsData = data;
      }

      if (!newsData) {
        throw new Error('Diff not found');
      }

      // Then get the page data
      const { data: pageData, error: pageError } = await supabase
        .from('app_pages')
        .select(`
          id,
          subslug,
          pricing_page_url,
          company_id,
          app_companies (
            id,
            slug,
            url,
            logo_url
          )
        `)
        .eq('id', newsData.page_id)
        .single();

      if (pageError) {
        console.error('Error fetching page:', pageError);
      }

      // Get all events for this news item from app_news_with_events
      const { data: eventsData, error: eventsError } = await supabase
        .from('app_news_with_events')
        .select('*')
        .eq('news_id', diffId);

      if (eventsError) {
        console.error('Error fetching events:', eventsError);
      }

      // Map events to EventChange format
      const changes: EventChange[] = (eventsData || []).map((row: any) => ({
        id: row.id,
        event_type: row.event_type,
        slug: row.slug,
        description: row.description,
        before_extracts: row.before_extracts,
        after_extract: row.after_extract,
        verified: row.verified || false,
        partially_correct: row.partially_correct || false,
        invalid: row.invalid || false,
        asset_id: row.asset_id,
        created_at: row.created_at,
        updated_at: row.updated_at
      }));

      // Construct the DiffDetail response
      const diff: DiffDetail = {
        id: newsData.id,
        page_id: newsData.page_id,
        slug: newsData.slug,
        v_before: newsData.v_before,
        v_after: newsData.v_after,
        asset_id: newsData.asset_id,
        hook: newsData.hook,
        description: newsData.description,
        published: newsData.published,
        created_at: newsData.created_at,
        updated_at: newsData.updated_at,
        changes: changes,
        page: pageData ? {
          id: pageData.id,
          subslug: pageData.subslug,
          pricing_page_url: pageData.pricing_page_url,
          company: Array.isArray(pageData.app_companies) ? pageData.app_companies[0] : pageData.app_companies
        } : undefined
      };

      return diff;
    },
    placeholderData,
    enabled: !!diffId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook for fetching all diffs for navigation (supports filtering)
 */
export function useDiffs(params?: {
  slug?: string;
  quarter?: string;
  event_type?: string;
  verification_status?: string;
  created_after?: string;
  published?: boolean;
  time_period?: string;
  limit?: number;
}, placeholderData?: DiffSummary[]) {
  const limit = params?.limit || 1000;

  return useQuery({
    queryKey: ['diffs', 'navigation', params],
    queryFn: async (): Promise<DiffSummary[]> => {
      // Use GraphQL fetchDiffs with filters for accurate navigation
      const response = await fetchDiffs(
        params?.slug,
        undefined, // page_id
        undefined, // v_before
        undefined, // v_after
        params?.quarter,
        params?.event_type,
        params?.verification_status,
        params?.created_after,
        params?.published,
        params?.time_period,
        limit,
        0 // offset
      );

      // Map to DiffSummary format
      return response.diffs.diffs.map(d => ({
        id: d.id,
        slug: d.slug,
        changes: d.changes.map(c => ({
          id: c.id,
          event_type: c.event_type,
          verified: c.verified || false,
          partially_correct: c.partially_correct || false,
          invalid: c.invalid || false
        }))
      }));
    },
    placeholderData,
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
  });
}

/**
 * Hook for diff-related mutations
 */
export function useDiffMutations() {
  const queryClient = useQueryClient();

  const verifyEventMutation = useMutation({
    mutationFn: ({ eventId, verified }: { eventId: string; verified: boolean }) =>
      updateEventVerified(eventId, verified),
    onSuccess: () => {
      queryClient.invalidateQueries(['diff']);
      queryClient.invalidateQueries(['diffs']);
    },
  });

  const partiallyCorrectEventMutation = useMutation({
    mutationFn: ({ eventId, partiallyCorrect }: { eventId: string; partiallyCorrect: boolean }) =>
      updateEventPartiallyCorrect(eventId, partiallyCorrect),
    onSuccess: () => {
      queryClient.invalidateQueries(['diff']);
      queryClient.invalidateQueries(['diffs']);
    },
  });

  const invalidEventMutation = useMutation({
    mutationFn: ({ eventId, invalid }: { eventId: string; invalid: boolean }) =>
      updateEventInvalid(eventId, invalid),
    onSuccess: () => {
      queryClient.invalidateQueries(['diff']);
      queryClient.invalidateQueries(['diffs']);
    },
  });

  const updateEventTypeMutation = useMutation({
    mutationFn: ({ eventId, eventType }: { eventId: string; eventType: string }) =>
      updateEventType(eventId, eventType),
    onSuccess: () => {
      queryClient.invalidateQueries(['diff']);
      queryClient.invalidateQueries(['diffs']);
    },
  });

  const updateEventDescriptionMutation = useMutation({
    mutationFn: ({ eventId, description }: { eventId: string; description: string }) =>
      updateEventDescription(eventId, description),
    onSuccess: () => {
      queryClient.invalidateQueries(['diff']);
      queryClient.invalidateQueries(['diffs']);
    },
  });

  const mergeEventsMutation = useMutation({
    mutationFn: ({ sourceEventId, targetEventId }: { sourceEventId: string; targetEventId: string }) =>
      mergeEvents(sourceEventId, targetEventId),
    onSuccess: () => {
      queryClient.invalidateQueries(['diff']);
      queryClient.invalidateQueries(['diffs']);
    },
  });

  const deleteEventMutation = useMutation({
    mutationFn: (eventId: string) => deleteEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries(['diff']);
      queryClient.invalidateQueries(['diffs']);
    },
  });

  const cloneEventMutation = useMutation({
    mutationFn: (eventId: string) => cloneEvent(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries(['diff']);
      queryClient.invalidateQueries(['diffs']);
    },
  });

  return {
    verifyEvent: (variables: { eventId: string; verified: boolean }, options?: any) =>
      verifyEventMutation.mutate(variables, options),
    partiallyCorrectEvent: (variables: { eventId: string; partiallyCorrect: boolean }, options?: any) =>
      partiallyCorrectEventMutation.mutate(variables, options),
    invalidEvent: (variables: { eventId: string; invalid: boolean }, options?: any) =>
      invalidEventMutation.mutate(variables, options),
    updateEventType: (variables: { eventId: string; eventType: string }, options?: any) =>
      updateEventTypeMutation.mutate(variables, options),
    updateEventDescription: async (variables: { eventId: string; description: string }) =>
      updateEventDescriptionMutation.mutateAsync(variables),
    mergeEvents: async (variables: { sourceEventId: string; targetEventId: string }) =>
      mergeEventsMutation.mutateAsync(variables),
    deleteEvent: (eventId: string, options?: any) =>
      deleteEventMutation.mutate(eventId, options),
    cloneEvent: async (eventId: string) =>
      cloneEventMutation.mutateAsync(eventId),
    isLoading: verifyEventMutation.isLoading ||
               partiallyCorrectEventMutation.isLoading ||
               invalidEventMutation.isLoading ||
               updateEventTypeMutation.isLoading ||
               updateEventDescriptionMutation.isLoading ||
               mergeEventsMutation.isLoading ||
               deleteEventMutation.isLoading ||
               cloneEventMutation.isLoading,
  };
}